import { Normalization } from '~/utils/normalization'

/**
 * Redacts sensitive values out of structured log payloads before they can reach any sink.
 *
 * Key matching is case-insensitive and delimiter-insensitive EXACT match, not substring --
 * this mirrors `LogRedactor::isSensitiveKey()` in mealz-api (app/Support/LogRedactor.php:88,
 * APP-1069/APP-1071), which itself switched from an original substring design specifically to
 * agree with Laravel Nightwatch's own `RedactsHeaders`/`RedactsRecords` traits
 * (`HeaderBag::has()` / `in_array(..., true)` -- both exact). A key that merely *contains* a
 * sensitive word (`user_email_verified_at`) must NOT redact: it is the property most likely to
 * silently regress, since both stacks must reach the same redact/don't-redact decision for the
 * same key.
 *
 * Delimiter-insensitivity is this file's own addition on top of that: this codebase writes
 * object keys in both `snake_case` (API payloads, forms) and `camelCase` (JS/TS variables --
 * `guestToken`, `accessToken`, `clientSecret`), and a list authored in one casing convention
 * would exact-match only that convention, silently missing the other. `access_token` and
 * `accessToken` normalize to the same `accesstoken` and are treated as the same key; that is
 * still exact match on the NORMALIZED key, not substring on the raw one --
 * `user_email_verified_at` normalizes to `useremailverifiedat`, which is not equal to
 * `email`'s normalized form, so it still does not redact (see the dedicated regression test).
 * The API side does not do this normalization (`in_array(strtolower($key), ...)` is
 * case-insensitive only), so a camelCase key redacted here and logged from Laravel under
 * the literal same name would NOT be redacted there -- a known, reported divergence, not
 * silently reconciled by this file (see the module's own commit/PR notes for the API-parity
 * write-up; APP-1071 carries the follow-up). This repo's list is strictly stricter than the
 * API's as a result, which is the safe direction for a mismatch to fail in.
 *
 * This module is a dependency-leaf utility in the `app/utils` directory category and
 * stays pure on purpose: it has no Nuxt/Vue runtime import and does not read `import.meta.dev`
 * itself. Whether redaction actually runs is passed in as the `enabled` boolean by the one
 * caller allowed to know about environment -- `app/composables/useLogger.ts` -- so this file
 * never has to guess what "development" means outside a Nuxt runtime (this matters for the
 * `node:test` suite covering this file directly, with no Nuxt/Vite build in the loop).
 */

const REDACTED = '[REDACTED]'
const CIRCULAR = '[Circular]'

/**
 * Case-insensitive, delimiter-insensitive exact-match key list (see `foldKey` below for what
 * "delimiter-insensitive" means and why it doesn't weaken exact match). Lives in exactly one
 * place -- nothing else in this module, or in `useLogger.ts`, reimplements or duplicates it
 * per call site or per level.
 *
 * Mirrors `logging.redact_fields`'s documented default in mealz-api (config/logging.php):
 * password, password_confirmation, token, access_token, refresh_token, secret, client_secret,
 * api_key, card_number, cvv, ssn, phone, email, address, from -- plus FE-specific keys added
 * deliberately, each with its own evidence below, that the API list has no reason to carry.
 * Four trace directly to this ticket's three named leaks; a further seven cover qualified PII
 * forms this codebase's own data shapes use that the bare API-mirrored words don't reach:
 *
 *   - `current_password`: ChangePassword.vue's actual submitted form field
 *     (`app/components/settings/security/ChangePassword.vue`, APP-592's leak site --
 *     `form.current_password`, logged verbatim at that file's own `console.log`) is not
 *     literally `password`/`password_confirmation`. Mirroring the API list verbatim, with
 *     nothing more, would leave this field un-redacted under exact matching.
 *   - `new_password`: NOT reachable at APP-592's own site -- that form's new-password field
 *     is bound to `form.password` (`new_password` is only the `<input name="...">` HTML
 *     attribute, never a submitted object key), so `password` already covers it there. Kept
 *     anyway as a common field name for password-change forms generally: a false-positive
 *     redaction costs nothing, a missed real one costs a credential, and no other form in
 *     this codebase uses it today (verified -- `grep -rn "new_password" app/` turns up only
 *     this file, an i18n key, and an HTML attribute name).
 *   - `guest_token`: the guest-cart session token's actual object key
 *     (the cart stores' own `{ guest_token: token }` shape), the
 *     credential APP-425 names directly. Distinct from `token` because `guest_token` is not
 *     a substring match away from it -- it needs its own list entry regardless of the
 *     delimiter-normalization added above.
 *   - `auth`: Laravel Echo / Pusher's private-channel authorizer response shape is
 *     `{ auth: "<appKey>:<signature>" }` -- a live WebSocket credential with no Laravel
 *     `Log::` call to mirror, so the API's list was never going to carry it. Added
 *     defensively: no Echo/Pusher plugin logs today (verified: the notifications-echo
 *     client plugin contains no `console.*` call), and this key exists
 *     so that the day one does, the credential is already covered rather than newly
 *     exposed. APP-459 describes a mount-time token log read out of a minified bundle and
 *     attributes it to Pusher; the only mount-time token log in source is the app root
 *     component's `guestToken`, which APP-425 already claims -- so treat APP-459's Pusher
 *     attribution as unconfirmed against source, not as the provenance of this key.
 *
 * A further seven keys cover QUALIFIED forms of concepts the base list only has the BARE
 * word for -- folding reconciles casing/delimiter variants of the SAME key, it does nothing
 * for a genuinely different key like `delivery_phone` next to bare `phone`. Each is a real
 * object key found carrying the sensitive value itself, not a name that merely contains a
 * sensitive-sounding word:
 *
 *   - `delivery_phone` / `delivery_contact_phone`: the customer's own phone number for an
 *     order, submitted at checkout, declared on every order-shaped type, and read off raw
 *     API responses by the order service at four call sites. Neither folds to `phone`.
 *   - `delivery_address`: the customer's own delivery address for an order
 *     (`app/pages/checkout/[slug].vue:1776`, `app/composables/useOrderDetailPage.ts:38`,
 *     `app/components/orders/EditOrderDetailsModal.vue:303`,
 *     `app/components/orders/detail/OrderInfoCard.vue:160`). Does not fold to `address`.
 *   - `formatted_address` / `full_address`: the generic geo-provider (Mapbox/Google Places)
 *     normalized-address output key (the geo-provider normalizer's `fullAddress`), read back
 *     off store, order, brand, and user records and consumed by several storefront and
 *     order-detail components. A store's own business address is arguably already
 *     public (it's shown on the storefront), but this is the SAME generic key the same
 *     geo-provider layer would produce for a customer's own searched address -- exact match
 *     can't tell which domain object it's attached to, and a redacted business address
 *     costs nothing while a missed customer one costs a real leak.
 *   - `organization_address`: the registrant's own business address at partner signup.
 *   - `google_places_address`: a store's saved Google Places address. Same
 *     public-business-address caveat as `formatted_address` above, kept for the same reason.
 *
 * Checked and deliberately EXCLUDED, because each carries no sensitive value despite a
 * sensitive-sounding name -- widening the list to catch these would redact useful debugging
 * signal for no protective benefit:
 *   - `credentials`: every occurrence in `app/**` is the Fetch API's
 *     `{ credentials: 'include' }` request option (a fixed literal, not a secret) or a
 *     `'credentials'` string-union member describing a client mode
 *     (`composables/useApiRequest.ts`) -- verified via
 *     `grep -rn "credentials" app/ | grep -vE "'include'|'omit'|'same-origin'"`, which
 *     returns only an unrelated error message and that same type declaration.
 *   - `isAuthenticated`: a boolean auth-STATE flag from `useSanctumAuth()`,
 *     never a credential value.
 *   - `card_brand`: a display string (`'visa'`/`'mastercard'`) from
 *     `app/pages/dashboard/payments/[id].vue:278`, not a PAN.
 *   - `email_verified_at` / `addressable_type`: metadata (a verification timestamp; a
 *     polymorphic relation discriminator) about the real field, not the value itself.
 *   - `cardRadius` (`StoreCard.vue`) and the `mastercard` KEY in an icon lookup map
 *     (`PaymentMethodCard.vue:107`, `{ mastercard: '/images/cards/mastercard.svg' }`) are UI
 *     styling/asset identifiers.
 * See the dedicated negative tests for `isAuthenticated` and `card_brand`, guarding against
 * a future well-meaning widening of this list on name resemblance alone.
 *
 * Structural limit, not solvable by extending this list (reported to the fleet, not fixed
 * here): a key-based mechanism cannot redact a credential logged as a bare positional value
 * or embedded in free text with no key at all --
 * the app root component's own pre-swap `console.log('...Token:', guestToken)` is exactly this shape.
 * The fix there is structural (the swap must pass `{ guestToken }` as a keyed payload, not a
 * positional string-concatenation argument), not a redaction-list entry.
 *
 * Extending this list is the correct fix for a newly-discovered sensitive field (same as
 * adding `phone`/`email` to `logging.redact_fields` was, per the API doc's own §1.5) -- not
 * a one-off inline check next to a call site.
 */
const SENSITIVE_KEYS: ReadonlySet<string> = new Set([
  'password',
  'password_confirmation',
  'current_password',
  'new_password',
  'guest_token',
  'token',
  'access_token',
  'refresh_token',
  'secret',
  'client_secret',
  'api_key',
  'card_number',
  'cvv',
  'ssn',
  'phone',
  'email',
  'address',
  'auth',
  'delivery_phone',
  'delivery_contact_phone',
  'delivery_address',
  'formatted_address',
  'full_address',
  'organization_address',
  'google_places_address'
])

/**
 * Case + delimiter fold: lowercase, then drop `_`/`-`. `access_token`, `accessToken`, and
 * `access-token` all fold to `accesstoken`. This is still exact match, just on the folded
 * key rather than the raw one -- `user_email_verified_at` folds to `useremailverifiedat`,
 * which is not `email`'s folded form (`email`), so it still does not redact. Folding a
 * multi-word key never merges it with an unrelated single-word one; it only reconciles the
 * two delimiter conventions this codebase actually writes the SAME key in.
 */
function foldKey(key: string): string {
  return key.toLowerCase().replace(/[_-]/g, '')
}

// Computed once at module load, not per lookup -- `isSensitiveKey` runs on every key of
// every logged object, across every redaction-enabled call in the app.
const FOLDED_SENSITIVE_KEYS: ReadonlySet<string> = new Set(Array.from(SENSITIVE_KEYS, foldKey))

function isSensitiveKey(key: string): boolean {
  return FOLDED_SENSITIVE_KEYS.has(foldKey(key))
}

type ErrorWithExtras = Error & Record<string, unknown>

/**
 * `Error` instances need deliberate handling because they don't survive the generic object
 * walk below: `name`/`message`/`stack` are non-enumerable on a native `Error`, so
 * `Object.entries()` sees none of them and a redacted Error would otherwise silently
 * collapse to `{}`, losing the entire signal a caller logged the error for.
 *
 * Unlike a PHP backtrace (`CheckoutController.php:90-95`'s `$e->getTrace()`, the highest-
 * severity finding in the API-side audit), a V8 `.stack` string does not embed function
 * *argument* values -- so it is not the same leak vector. It does still embed internal file
 * paths and call structure, which is still development's business to see in full and no
 * one else's, matching the API's own local/testing-vs-everywhere-else split (§3.2) -- so
 * `stack` is dropped unconditionally here (this function only ever runs when redaction is
 * enabled; see `redactForLog` below).
 *
 * Custom own-enumerable properties -- an Axios-style error carrying a `.response` with the
 * full request/response body is the realistic case -- are walked through the exact same
 * redaction as everything else, because that is precisely where a real secret is most
 * likely to be hiding on an Error object.
 */
function redactError(error: Error, seen: Set<object>): Record<string, unknown> | typeof CIRCULAR {
  if (seen.has(error)) return CIRCULAR
  seen.add(error)

  try {
    const extras = error as ErrorWithExtras
    const own: Record<string, unknown> = {}

    for (const key of Object.keys(error)) {
      if (key === 'name' || key === 'message' || key === 'stack') continue
      own[key] = isSensitiveKey(key) ? REDACTED : walk(extras[key], seen)
    }

    return { name: error.name, message: error.message, ...own }
  } finally {
    seen.delete(error)
  }
}

/**
 * `seen` tracks the current ancestor path (added on entry, removed via `finally` on exit) --
 * not every object visited across the whole traversal. That distinction matters: two
 * properties that both point at the same shared-but-not-nested object (e.g. two fields
 * referencing one common lookup object) are not a cycle and must not be flattened to
 * `[Circular]`; only a value that is its own ancestor is.
 *
 * `Date` is special-cased ahead of the generic record branch for the same reason `Error`
 * is: it is `typeof 'object'`, not an array, and (per `Normalization.isRecord`) would
 * otherwise be treated as a record -- but it carries no own-enumerable properties, so
 * without this branch every Date in a payload silently collapses to `{}`.
 *
 * Exotic non-plain-object values this function does NOT special-case (`Map`, `Set`,
 * `RegExp`, arbitrary class instances) fall into the same record branch and lose any
 * content that isn't an own-enumerable property, for the same structural reason as Date --
 * a known boundary, not a bug. This ticket's payloads are plain JSON-shaped objects/arrays,
 * matching the API-parity contract; the day a log call needs one of those collection types
 * preserved is the day to extend this function, not before.
 */
function walk(value: unknown, seen: Set<object>): unknown {
  if (value === null || value === undefined) return value
  if (typeof value !== 'object') return value
  if (value instanceof Date) return value
  if (value instanceof Error) return redactError(value, seen)

  if (Array.isArray(value)) {
    if (seen.has(value)) return CIRCULAR
    seen.add(value)
    try {
      return value.map((item) => walk(item, seen))
    } finally {
      seen.delete(value)
    }
  }

  if (Normalization.isRecord(value)) {
    if (seen.has(value)) return CIRCULAR
    seen.add(value)
    try {
      const result: Record<string, unknown> = {}
      for (const [key, item] of Object.entries(value)) {
        result[key] = isSensitiveKey(key) ? REDACTED : walk(item, seen)
      }
      return result
    } finally {
      seen.delete(value)
    }
  }

  return value
}

/**
 * Redact a structured log payload. `enabled` is the sole redaction switch, injected by the
 * caller rather than read here (see the module-level comment) -- when `false`, this is a
 * pure identity pass-through: no traversal happens at all, so a cyclic value handed through
 * in development costs nothing and risks nothing, matching "redaction is inert in
 * development" precisely rather than approximating it with a no-op walk.
 */
export function redactForLog(value: unknown, enabled: boolean): unknown {
  if (!enabled) return value
  return walk(value, new Set())
}
